import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../api/apiClient';

export interface ProjectMember {
  employee: { _id: string; username: string; email: string };
  role: string;
  allocation: number;
  billingType: string;
  isApprover: boolean;
}

export interface Project {
  _id: string;
  name: string;
  description?: string;
  client?: { _id: string; companyName: string; contactEmail?: string };
  department?: { _id: string; name: string };
  manager: { _id: string; username: string; email: string };
  createdBy?: { _id: string; username: string; email: string };
  members: ProjectMember[];
  status: 'Active' | 'Completed' | 'On Hold' | 'Archived';
  progress: number;
  progressNotes: { text: string; updatedBy: any; date: string }[];
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Payload type used when creating/updating
export interface ProjectPayload {
  name?: string;
  description?: string;
  client?: string;
  department?: string;
  manager?: string;
  status?: string;
  progress?: number;
  progressNotes?: { text: string; updatedBy?: string; date?: string }[];
  startDate?: string;
  endDate?: string;
  members?: {
    employee: string;
    role: string;
    allocation: number;
    billingType: string;
    isApprover: boolean;
  }[];
}

interface ProjectState {
  projects: Project[];
  loading: boolean;
  error: string | null;
}

const initialState: ProjectState = {
  projects: [],
  loading: false,
  error: null,
};

export const fetchProjects = createAsyncThunk('projects/fetchProjects', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get('/projects');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch projects');
  }
});

export const createProject = createAsyncThunk('projects/createProject', async (projectData: ProjectPayload, { rejectWithValue }) => {
  try {
    const response = await apiClient.post('/projects', projectData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create project');
  }
});

export const updateProject = createAsyncThunk('projects/updateProject', async ({ id, data }: { id: string; data: ProjectPayload }, { rejectWithValue }) => {
  try {
    const response = await apiClient.put(`/projects/${id}`, data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update project');
  }
});

export const deleteProject = createAsyncThunk('projects/deleteProject', async (id: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/projects/${id}`);
    return id;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete project');
  }
});

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createProject.fulfilled, (state, action) => {
        state.projects.unshift(action.payload);
      })
      // Update
      .addCase(updateProject.fulfilled, (state, action) => {
        const index = state.projects.findIndex((p) => p._id === action.payload._id);
        if (index !== -1) state.projects[index] = action.payload;
      })
      // Delete
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter((p) => p._id !== action.payload);
      });
  },
});

export default projectSlice.reducer;
